// Top 5 agents by cost in the current billing window — rendered as a
// list-with-progress-bar rather than a Recharts BarChart. The bar chart
// version was ambiguous on first read (the X-axis dollar ticks didn't
// say "USD spend", and a near-zero bar against an auto-scaled axis was
// invisible), so the pattern here mirrors how Stripe / Linear / Vercel
// surface "top X" data: each row shows the name, the exact value, the
// secondary metric (runs), and a relative bar so a quick scan still
// reveals the leader.
//
// Deleted-agent rows (`agentId === null`, kept in `byAgent` so the
// per-agent sum still equals the period total — see UsageByAgentSchema)
// are filtered out here: drill-down would 404, and "(deleted)" doesn't
// belong on a 5-row "top performers" board. The Usage page still shows
// them in its full breakdown table.

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot } from 'lucide-react';
import type { UsageByAgent, UsageSummary } from '@cortex/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { EmptyState } from '../ui/empty-state.js';
import { formatUsd } from '../../lib/format-money.js';

interface TopAgentsCardProps {
  summary: UsageSummary;
}

interface TopAgentRow {
  agentId: string;
  agentName: string;
  costUsd: number;
  runs: number;
}

function isNamed(row: UsageByAgent): row is UsageByAgent & {
  agentId: string;
  agentName: string;
} {
  return row.agentId !== null && row.agentName !== null;
}

export function TopAgentsCard({ summary }: TopAgentsCardProps) {
  const navigate = useNavigate();

  const rows = useMemo<TopAgentRow[]>(() => {
    const named = summary.byAgent.filter(isNamed);
    // [...].sort() rather than `.toSorted()` to match the project's
    // tsconfig lib target (es2022); same pattern usage/page.tsx uses.
    const sorted = [...named].sort((a, b) => b.costUsd - a.costUsd);
    return sorted.slice(0, 5).map((r) => ({
      agentId: r.agentId,
      agentName: r.agentName,
      costUsd: r.costUsd,
      runs: r.runs,
    }));
  }, [summary.byAgent]);

  // Normalize bar width against the leader so the relative ranking is
  // legible even when one agent dwarfs the rest. When every agent is at
  // 0 cost (period just started, no traffic) we draw empty bars and
  // rely on the per-row labels to convey the state.
  const maxCost = useMemo(
    () => rows.reduce((m, r) => (r.costUsd > m ? r.costUsd : m), 0),
    [rows],
  );

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Top agents</CardTitle>
        <CardDescription>
          Highest spend this billing period. Click a row to inspect the agent.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState
            icon={Bot}
            title="No agent activity yet"
            description="Once an agent handles its first run, it will appear here ranked by cost."
            className="border-0 bg-transparent py-6"
          />
        ) : (
          <ul role="list" className="space-y-4" data-testid="home-top-agents">
            {rows.map((row) => (
              <TopAgentRow
                key={row.agentId}
                row={row}
                maxCost={maxCost}
                onClick={() => {
                  navigate(`/agents/${row.agentId}`);
                }}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function TopAgentRow({
  row,
  maxCost,
  onClick,
}: {
  row: TopAgentRow;
  maxCost: number;
  onClick: () => void;
}) {
  const widthPct = maxCost > 0 ? (row.costUsd / maxCost) * 100 : 0;
  const runsLabel = row.runs === 1 ? '1 run' : `${row.runs.toLocaleString()} runs`;

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="group block w-full rounded-md text-left transition-colors hover:bg-accent/40 focus-visible:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <div className="flex items-center justify-between gap-3 px-2 pt-1">
          <span className="truncate text-sm font-medium">{row.agentName}</span>
          <span className="shrink-0 font-mono text-sm tabular-nums">
            {formatUsd(row.costUsd)}
          </span>
        </div>
        <div className="flex items-center gap-3 px-2 pb-1 pt-1.5">
          <div
            className="h-2 flex-1 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label={`${row.agentName} spend`}
            aria-valuenow={Math.round(widthPct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${widthPct.toFixed(1)}%` }}
            />
          </div>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {runsLabel}
          </span>
        </div>
      </button>
    </li>
  );
}
